/**
 * QuestOfferManager - Handles NPC quest offering and management
 * Extracted from NPCInteractionUI.tsx
 */

import { NPC, Quest } from '../../../../shared/NPCData';

export interface QuestOfferState {
  availableQuests: Quest[];
  selectedQuest: Quest | null;
  playerLevel: number;
}

export class QuestOfferManager {
  private state: QuestOfferState;
  private onQuestAccept?: (quest: Quest) => void;
  private onQuestComplete?: (quest: Quest) => void;
  
  constructor(
    npc: NPC, 
    playerLevel: number,
    onQuestAccept?: (quest: Quest) => void,
    onQuestComplete?: (quest: Quest) => void
  ) {
    this.onQuestAccept = onQuestAccept;
    this.onQuestComplete = onQuestComplete;
    
    this.state = {
      availableQuests: npc.quests || [],
      selectedQuest: null,
      playerLevel
    };
  }
  
  /**
   * Select a quest for detailed view
   */
  selectQuest(quest: Quest): void {
    this.state.selectedQuest = quest;
  }
  
  /**
   * Accept a quest
   */
  acceptQuest(questId: string): Quest | null {
    const quest = this.state.availableQuests.find(q => q.id === questId);
    if (!quest) return null;
    
    if (this.onQuestAccept) {
      this.onQuestAccept(quest);
    }
    
    // Remove from available quests
    this.state.availableQuests = this.state.availableQuests.filter(q => q.id !== questId);
    
    return quest;
  }
  
  /**
   * Complete a quest
   */
  completeQuest(questId: string): Quest | null {
    const quest = this.state.availableQuests.find(q => q.id === questId);
    if (!quest) return null;
    
    if (this.onQuestComplete) {
      this.onQuestComplete(quest);
    }
    
    // Remove from available quests (if repeatable, would handle differently)
    this.state.availableQuests = this.state.availableQuests.filter(q => q.id !== questId);
    
    return quest;
  }
  
  /**
   * Check if quest is available to player
   */
  isQuestAvailable(quest: Quest): boolean {
    if (!quest) return false;
    if (quest.isAccepted || quest.isCompleted) return false;
    if (this.state.playerLevel < quest.requiredLevel) return false;
    return true;
  }
  
  /**
   * Check if quest is completable
   */
  isQuestCompletable(quest: Quest): boolean {
    if (!quest) return false;
    return quest.isAccepted && quest.isTurnInReady && !quest.isCompleted;
  }
  
  /**
   * Get available quests for player level
   */
  getAvailableQuests(): Quest[] {
    return this.state.availableQuests.filter(quest => this.isQuestAvailable(quest));
  }
  
  /**
   * Get quest rewards description
   */
  getQuestRewards(quest: Quest): string {
    const rewards: string[] = [];
    
    if (quest.rewardXP) {
      rewards.push(`${quest.rewardXP} XP`);
    }
    
    if (quest.rewardGold) {
      rewards.push(`${quest.rewardGold} Gold`);
    }
    
    if (quest.rewardItems && quest.rewardItems.length > 0) {
      rewards.push(`${quest.rewardItems.length} Items`);
    }
    
    return rewards.join(', ') || 'No rewards';
  }
  
  /**
   * Get quest difficulty color
   */
  getQuestDifficultyColor(quest: Quest): string {
    const levelDiff = quest.requiredLevel - this.state.playerLevel;
    
    if (levelDiff > 5) return '#ff0000'; // Red - very difficult
    if (levelDiff > 2) return '#ff6600'; // Orange - difficult
    if (levelDiff > -2) return '#ffff00'; // Yellow - normal
    if (levelDiff > -5) return '#00ff00'; // Green - easy
    return '#888888'; // Gray - trivial
  }
  
  /**
   * Get quest state
   */
  getState(): QuestOfferState {
    return {
      availableQuests: [...this.state.availableQuests],
      selectedQuest: this.state.selectedQuest,
      playerLevel: this.state.playerLevel
    };
  }
  
  /**
   * Update player level (for quest availability)
   */
  updatePlayerLevel(level: number): void {
    this.state.playerLevel = level;
  }
}
